import {
  ceilHugeAtLeast,
  compareHuge,
  huge,
  hugeOne,
  hugeZero,
  type HugeNumber,
} from "@/shared/numbers";
import {
  UPGRADE_MILESTONE_INTERVAL,
  isUpgradeMilestonePurchase,
} from "@/features/hoverIdle/core/upgradeMilestones";
import type { HovGainUpgradeBulkPurchaseMilestoneRecord } from "@/features/hoverIdle/types";
import {
  ONE_COST_HOVER_REPEATER_BATCH_MAX_PURCHASES_PER_FIRE,
  advanceOneCostPlateauRawCost,
  resolveOneCostBulkPurchaseExactCount,
} from "./oneCostBulkPurchase";

export type HovGainUpgradePurchaseMode = "single" | "one-cost-bulk";

export interface HovGainUpgradePurchasePlannerInput {
  readonly mode: HovGainUpgradePurchaseMode;
  readonly upgradeAvailable: boolean;
  readonly oneCostBatchEnabled: boolean;
  readonly availableHov: HugeNumber;
  readonly currentEnhancementLevel: number;
  readonly currentRawCost: HugeNumber;
  readonly rawCostMultiplier: number;
  readonly effectiveCostMultiplier: HugeNumber;
  readonly cap?: number;
  // Maximum number of enhancement levels a single planner invocation may
  // purchase outside the one-cost plateau. Sourced from the Price Wall
  // simultaneous-purchase reward; defaults to 1 so callers without that
  // input keep the existing "single click = one purchase" semantics. The
  // one-cost plateau path (cost == 1, bulk mode, batch flag enabled) ignores
  // this cap and continues to use the existing bulk ceiling.
  readonly simultaneousPurchaseCap?: number;
}

export interface HovGainUpgradePurchasePlanMilestoneRecord {
  readonly enhancementLevelBefore: number;
  readonly enhancementLevelAfter: number;
  readonly effectiveCostPaid: HugeNumber;
}

export interface HovGainUpgradePurchasePlan {
  readonly ok: boolean;
  readonly purchaseCount: number;
  readonly enhancementLevelBefore: number;
  readonly enhancementLevelAfter: number;
  readonly firstEffectiveCost: HugeNumber;
  readonly spentHov: HugeNumber;
  readonly rawCostAfter: HugeNumber;
  readonly milestonePurchases: readonly HovGainUpgradePurchasePlanMilestoneRecord[];
}

export interface HovGainUpgradeAutomationSegmentPlan extends HovGainUpgradePurchasePlan {
  readonly attemptsRequested: number;
  readonly attemptsApplied: number;
  readonly levelBudget: number;
}

export function resolveHovGainUpgradeAutomationSegmentPlan(
  input: HovGainUpgradePurchasePlannerInput,
  attemptBudget: number,
): HovGainUpgradeAutomationSegmentPlan {
  const attemptsRequested = Math.max(0, Math.floor(attemptBudget));
  const firstEffectiveCost = resolveEffectiveCost(
    input.currentRawCost,
    input.effectiveCostMultiplier,
  );
  if (attemptsRequested <= 0) {
    return {
      ...createNoOpPlan(input, firstEffectiveCost),
      attemptsRequested,
      attemptsApplied: 0,
      levelBudget: 0,
    };
  }

  const simultaneousPurchaseCap = resolveSimultaneousPurchaseCap(
    input.simultaneousPurchaseCap,
  );
  const oneCostLevelsPerAttempt = resolveOneCostBulkCap(input.mode, input.cap);
  const plateauEligible = isPlateauEligible(
    input,
    firstEffectiveCost,
    oneCostLevelsPerAttempt,
  );
  let plateauLevelBudget = 0;
  let plateauAttemptsReserved = 0;

  if (plateauEligible) {
    plateauLevelBudget = resolveOneCostBulkPurchaseExactCount({
      availableHov: input.availableHov,
      currentRawCost: input.currentRawCost,
      rawCostMultiplier: input.rawCostMultiplier,
      effectiveCostMultiplier: input.effectiveCostMultiplier,
      cap: attemptsRequested * oneCostLevelsPerAttempt,
    });
    plateauAttemptsReserved =
      plateauLevelBudget <= 0
        ? 0
        : Math.ceil(plateauLevelBudget / oneCostLevelsPerAttempt);
  }

  const remainingAttempts = Math.max(
    0,
    attemptsRequested - plateauAttemptsReserved,
  );
  const levelBudget =
    plateauLevelBudget + remainingAttempts * simultaneousPurchaseCap;
  const purchasePlan = resolveHovGainUpgradeAggregatePlan(input, levelBudget);
  const plateauLevelsApplied = Math.min(
    purchasePlan.purchaseCount,
    plateauLevelBudget,
  );
  const nonPlateauLevelsApplied = Math.max(
    0,
    purchasePlan.purchaseCount - plateauLevelsApplied,
  );
  const attemptsApplied =
    (plateauLevelsApplied > 0
      ? Math.ceil(plateauLevelsApplied / oneCostLevelsPerAttempt)
      : 0) +
    (nonPlateauLevelsApplied > 0
      ? Math.ceil(nonPlateauLevelsApplied / simultaneousPurchaseCap)
      : 0);

  return {
    ...purchasePlan,
    attemptsRequested,
    attemptsApplied: Math.min(attemptsRequested, attemptsApplied),
    levelBudget,
  };
}

export function resolveHovGainUpgradePurchasePlan(
  input: HovGainUpgradePurchasePlannerInput,
): HovGainUpgradePurchasePlan {
  const firstEffectiveCost = resolveEffectiveCost(
    input.currentRawCost,
    input.effectiveCostMultiplier,
  );

  if (
    !input.upgradeAvailable ||
    compareHuge(input.availableHov, firstEffectiveCost) < 0
  ) {
    return createNoOpPlan(input, firstEffectiveCost);
  }

  const cap = resolveOneCostBulkCap(input.mode, input.cap);
  const plateauEligible = isPlateauEligible(input, firstEffectiveCost, cap);

  if (!plateauEligible) {
    return createMultiPurchasePlan(input, firstEffectiveCost);
  }

  const plateauCount = resolvePlateauPurchaseCount(
    input,
    firstEffectiveCost,
    cap,
  );
  const totalCount = 1 + plateauCount;
  const spentHov = firstEffectiveCost.add(plateauCount);

  return {
    ok: true,
    purchaseCount: totalCount,
    enhancementLevelBefore: input.currentEnhancementLevel,
    enhancementLevelAfter: input.currentEnhancementLevel + totalCount,
    firstEffectiveCost,
    spentHov,
    rawCostAfter: advanceRawCostByPurchaseCount(
      input.currentRawCost,
      input.rawCostMultiplier,
      totalCount,
    ),
    milestonePurchases: collectPlateauMilestoneRecords(
      input.currentEnhancementLevel,
      totalCount,
      firstEffectiveCost,
    ),
  };
}

export function resolveEffectiveHovGainUpgradeCost(
  currentRawCost: HugeNumber,
  effectiveCostMultiplier: HugeNumber,
): HugeNumber {
  return resolveEffectiveCost(currentRawCost, effectiveCostMultiplier);
}

/**
 * The exact ≥1-purchase gate every planner entry point shares (mirrors the
 * `!input.upgradeAvailable || availableHov < firstEffectiveCost` no-op check
 * in {@link resolveHovGainUpgradePurchasePlan} and
 * {@link resolveHovGainUpgradeAggregatePlan}): a plan buys nothing
 * unless the target is available AND `availableHov` is at least this value.
 * Returns `null` when the target cannot buy regardless of `availableHov`
 * (unavailable / blocked), so callers projecting a future affordability
 * crossing can distinguish "unreachable target" from "reachable threshold".
 * Independent of `availableHov`, `mode`, and `cap`: the cost side of the
 * gate depends only on `currentRawCost` and `effectiveCostMultiplier`.
 */
export function resolveHovGainUpgradeFirstPurchaseThreshold(
  input: HovGainUpgradePurchasePlannerInput,
): HugeNumber | null {
  if (!input.upgradeAvailable) {
    return null;
  }
  return resolveEffectiveCost(
    input.currentRawCost,
    input.effectiveCostMultiplier,
  );
}

/**
 * Aggregate purchase plan used by bounded gameplay advancement. It buys up to
 * `levelBudget` enhancement levels in one pass, using the same progressive
 * cost growth and affordability rule as the single-purchase path. The returned
 * plan is applied through the
 * same `applyGlobalHovGainPurchasePlan` / `applyHovCellGainPurchasePlan` paths
 * the single purchases used, so reward, cost growth, cumulative stats, unlock
 * records, and (global) milestone records stay identical.
 *
 * The effective-cost-1 plateau is resolved through the existing closed-form
 * bulk helper ({@link resolveOneCostBulkPurchaseExactCount}) instead of stepping
 * level by level, so a long plateau cannot stall a bounded advancement. Outside
 * the plateau the geometric cost growth bounds the level count logarithmically,
 * so the level-by-level walk stays cheap.
 */
export function resolveHovGainUpgradeAggregatePlan(
  input: HovGainUpgradePurchasePlannerInput,
  levelBudget: number,
): HovGainUpgradePurchasePlan {
  const firstEffectiveCost = resolveEffectiveCost(
    input.currentRawCost,
    input.effectiveCostMultiplier,
  );
  const budget = Math.max(0, Math.floor(levelBudget));
  if (
    !input.upgradeAvailable ||
    budget <= 0 ||
    compareHuge(input.availableHov, firstEffectiveCost) < 0
  ) {
    return createNoOpPlan(input, firstEffectiveCost);
  }

  let purchasedLevels = 0;
  let spentHov = hugeZero();
  let rawCost = input.currentRawCost;
  let remainingHov = input.availableHov;
  const milestoneRecords: HovGainUpgradePurchasePlanMilestoneRecord[] = [];

  while (purchasedLevels < budget) {
    const effectiveCost = resolveEffectiveCost(
      rawCost,
      input.effectiveCostMultiplier,
    );
    if (compareHuge(remainingHov, effectiveCost) < 0) break;

    if (compareHuge(effectiveCost, hugeOne()) === 0) {
      const remainingBudget = budget - purchasedLevels;
      const affordableCap = resolveAffordableOneCostBudget(
        remainingHov,
        remainingBudget,
      );
      const plateauCount = resolveOneCostBulkPurchaseExactCount({
        availableHov: remainingHov,
        currentRawCost: rawCost,
        rawCostMultiplier: input.rawCostMultiplier,
        effectiveCostMultiplier: input.effectiveCostMultiplier,
        cap: affordableCap,
      });
      if (plateauCount <= 0) break;
      appendOneCostPlateauMilestoneRecords(
        milestoneRecords,
        input.currentEnhancementLevel + purchasedLevels,
        plateauCount,
      );
      purchasedLevels += plateauCount;
      spentHov = spentHov.add(plateauCount);
      remainingHov = remainingHov.sub(plateauCount);
      rawCost = advanceRawCostByPurchaseCount(
        rawCost,
        input.rawCostMultiplier,
        plateauCount,
      );
      // Plateau ended on budget / Hov, not on cost rising past 1: nothing more
      // is affordable at cost 1, so stop without re-growing rawCost.
      if (plateauCount >= affordableCap) break;
      continue;
    }

    const enhancementLevelBefore =
      input.currentEnhancementLevel + purchasedLevels;
    if (isUpgradeMilestonePurchase(enhancementLevelBefore)) {
      milestoneRecords.push({
        enhancementLevelBefore,
        enhancementLevelAfter: enhancementLevelBefore + 1,
        effectiveCostPaid: effectiveCost,
      });
    }
    spentHov = spentHov.add(effectiveCost);
    remainingHov = remainingHov.sub(effectiveCost);
    rawCost = ceilHugeAtLeast(rawCost.mul(input.rawCostMultiplier), 1);
    purchasedLevels += 1;
  }

  if (purchasedLevels <= 0) {
    return createNoOpPlan(input, firstEffectiveCost);
  }

  return {
    ok: true,
    purchaseCount: purchasedLevels,
    enhancementLevelBefore: input.currentEnhancementLevel,
    enhancementLevelAfter: input.currentEnhancementLevel + purchasedLevels,
    firstEffectiveCost,
    spentHov,
    rawCostAfter: rawCost,
    milestonePurchases: milestoneRecords,
  };
}

export function toBulkMilestoneRecords(
  plan: HovGainUpgradePurchasePlan,
): readonly HovGainUpgradeBulkPurchaseMilestoneRecord[] {
  return plan.milestonePurchases.map((record) => ({
    enhancementLevelBefore: record.enhancementLevelBefore,
    enhancementLevelAfter: record.enhancementLevelAfter,
  }));
}

function resolveEffectiveCost(
  rawCost: HugeNumber,
  effectiveCostMultiplier: HugeNumber,
): HugeNumber {
  return ceilHugeAtLeast(rawCost.mul(effectiveCostMultiplier), 1);
}

function resolveOneCostBulkCap(
  mode: HovGainUpgradePurchaseMode,
  cap: number | undefined,
): number {
  if (mode === "single") return 1;
  return cap ?? ONE_COST_HOVER_REPEATER_BATCH_MAX_PURCHASES_PER_FIRE;
}

function resolveSimultaneousPurchaseCap(
  simultaneousPurchaseCap: number | undefined,
): number {
  if (
    simultaneousPurchaseCap === undefined ||
    !Number.isFinite(simultaneousPurchaseCap)
  ) {
    return 1;
  }
  return Math.max(1, Math.floor(simultaneousPurchaseCap));
}

function isPlateauEligible(
  input: HovGainUpgradePurchasePlannerInput,
  firstEffectiveCost: HugeNumber,
  cap: number,
): boolean {
  if (input.mode !== "one-cost-bulk") return false;
  if (!input.oneCostBatchEnabled) return false;
  if (cap <= 1) return false;
  return compareHuge(firstEffectiveCost, 1) === 0;
}

function resolvePlateauPurchaseCount(
  input: HovGainUpgradePurchasePlannerInput,
  firstEffectiveCost: HugeNumber,
  cap: number,
): number {
  const rawCostAfterFirst = ceilHugeAtLeast(
    input.currentRawCost.mul(input.rawCostMultiplier),
    1,
  );
  const availableHovAfterFirst = input.availableHov.sub(firstEffectiveCost);
  return resolveOneCostBulkPurchaseExactCount({
    availableHov: availableHovAfterFirst,
    currentRawCost: rawCostAfterFirst,
    rawCostMultiplier: input.rawCostMultiplier,
    effectiveCostMultiplier: input.effectiveCostMultiplier,
    cap: cap - 1,
  });
}

// Outside the one-cost plateau, walk up to `simultaneousPurchaseCap` purchases
// at the progressive raw cost. The first purchase is always made (caller
// already confirmed affordability); subsequent purchases stop when either the
// cap is reached or remaining Hov falls below the next effective cost.
function createMultiPurchasePlan(
  input: HovGainUpgradePurchasePlannerInput,
  firstEffectiveCost: HugeNumber,
): HovGainUpgradePurchasePlan {
  const cap = resolveSimultaneousPurchaseCap(input.simultaneousPurchaseCap);
  let purchaseCount = 1;
  let spentHov = firstEffectiveCost;
  let rawCost = input.currentRawCost;
  const milestoneRecords: HovGainUpgradePurchasePlanMilestoneRecord[] = [];
  appendMilestoneRecordIfTriggered(
    milestoneRecords,
    input.currentEnhancementLevel,
    firstEffectiveCost,
  );

  while (purchaseCount < cap) {
    rawCost = ceilHugeAtLeast(rawCost.mul(input.rawCostMultiplier), 1);
    const nextEffectiveCost = resolveEffectiveCost(
      rawCost,
      input.effectiveCostMultiplier,
    );
    const remaining = input.availableHov.sub(spentHov);
    if (compareHuge(remaining, nextEffectiveCost) < 0) break;
    spentHov = spentHov.add(nextEffectiveCost);
    appendMilestoneRecordIfTriggered(
      milestoneRecords,
      input.currentEnhancementLevel + purchaseCount,
      nextEffectiveCost,
    );
    purchaseCount += 1;
  }

  return {
    ok: true,
    purchaseCount,
    enhancementLevelBefore: input.currentEnhancementLevel,
    enhancementLevelAfter: input.currentEnhancementLevel + purchaseCount,
    firstEffectiveCost,
    spentHov,
    rawCostAfter: advanceRawCostByPurchaseCount(
      input.currentRawCost,
      input.rawCostMultiplier,
      purchaseCount,
    ),
    milestonePurchases: milestoneRecords,
  };
}

function createNoOpPlan(
  input: HovGainUpgradePurchasePlannerInput,
  firstEffectiveCost: HugeNumber,
): HovGainUpgradePurchasePlan {
  return {
    ok: false,
    purchaseCount: 0,
    enhancementLevelBefore: input.currentEnhancementLevel,
    enhancementLevelAfter: input.currentEnhancementLevel,
    firstEffectiveCost,
    spentHov: hugeZero(),
    rawCostAfter: input.currentRawCost,
    milestonePurchases: [],
  };
}

function appendMilestoneRecordIfTriggered(
  records: HovGainUpgradePurchasePlanMilestoneRecord[],
  enhancementLevelBefore: number,
  effectiveCostPaid: HugeNumber,
): void {
  if (!isUpgradeMilestonePurchase(enhancementLevelBefore)) return;
  records.push({
    enhancementLevelBefore,
    enhancementLevelAfter: enhancementLevelBefore + 1,
    effectiveCostPaid,
  });
}

// Levels affordable at the cost-1 plateau, where each level costs exactly 1
// Hov, bounded by the remaining aggregate level budget.
function resolveAffordableOneCostBudget(
  remainingHov: HugeNumber,
  remainingBudget: number,
): number {
  if (compareHuge(remainingHov, remainingBudget) >= 0) return remainingBudget;
  return Math.max(0, Math.floor(remainingHov.toNumber()));
}

function advanceRawCostByPurchaseCount(
  rawCost: HugeNumber,
  rawCostMultiplier: number,
  purchaseCount: number,
): HugeNumber {
  const remaining = Math.max(0, Math.floor(purchaseCount));
  if (remaining === 0) {
    return rawCost;
  }

  // Preserve exact ceil recurrence while it materially affects integer costs.
  // Once the value is beyond JS integer precision, break_eternity cannot
  // represent the unit-sized rounding difference and the recurrence reduces
  // to geometric growth. This keeps million-level plateau batches bounded.
  const preserveExactSequence = remaining <= 4_096;
  const advanced = advanceOneCostPlateauRawCost(
    rawCost,
    rawCostMultiplier,
    remaining,
    preserveExactSequence ? null : huge(Number.MAX_SAFE_INTEGER),
  );
  const foldedSteps = remaining - advanced.stepsTaken;
  if (foldedSteps > 0) {
    return ceilHugeAtLeast(
      advanced.rawCost.mul(huge(rawCostMultiplier).pow(foldedSteps)),
      1,
    );
  }
  return advanced.rawCost;
}

// Plateau levels all cost exactly 1, so every milestone purchase inside the
// batch pays 1. Cheap integer scan; only global purchases consume these
// records, and milestones land once per interval.
function appendOneCostPlateauMilestoneRecords(
  records: HovGainUpgradePurchasePlanMilestoneRecord[],
  startEnhancementLevel: number,
  count: number,
): void {
  const milestoneRemainder = UPGRADE_MILESTONE_INTERVAL - 1;
  const startRemainder = startEnhancementLevel % UPGRADE_MILESTONE_INTERVAL;
  let offset =
    (milestoneRemainder - startRemainder + UPGRADE_MILESTONE_INTERVAL) %
    UPGRADE_MILESTONE_INTERVAL;
  for (; offset < count; offset += UPGRADE_MILESTONE_INTERVAL) {
    const enhancementLevelBefore = startEnhancementLevel + offset;
    records.push({
      enhancementLevelBefore,
      enhancementLevelAfter: enhancementLevelBefore + 1,
      effectiveCostPaid: hugeOne(),
    });
  }
}

function collectPlateauMilestoneRecords(
  enhancementLevelBefore: number,
  purchaseCount: number,
  firstEffectiveCost: HugeNumber,
): readonly HovGainUpgradePurchasePlanMilestoneRecord[] {
  const records: HovGainUpgradePurchasePlanMilestoneRecord[] = [];
  const milestoneRemainder = UPGRADE_MILESTONE_INTERVAL - 1;
  const startRemainder = enhancementLevelBefore % UPGRADE_MILESTONE_INTERVAL;
  let k =
    (milestoneRemainder - startRemainder + UPGRADE_MILESTONE_INTERVAL) %
    UPGRADE_MILESTONE_INTERVAL;
  for (; k < purchaseCount; k += UPGRADE_MILESTONE_INTERVAL) {
    const levelBefore = enhancementLevelBefore + k;
    records.push({
      enhancementLevelBefore: levelBefore,
      enhancementLevelAfter: levelBefore + 1,
      effectiveCostPaid: k === 0 ? firstEffectiveCost : hugeOne(),
    });
  }
  return records;
}
